// ./src/components/features/Applications/ApplicationAnswersSection.tsx
//
// "What they told you" — an applicant's answers beside the questions they were asked. Shared by the sponsorship
// (#A4.5) and volunteer (#A4.6) inboxes: one question engine, one way of reading answers back.
//
// ⚠ Answers are loaded BY SUBJECT (the rail's `loadAnswers`), never by question alone — one table holds guests',
// sponsors' and volunteers' answers, so a lookup without the subject returns somebody else's.
// ⚠ Unanswered questions are still SHOWN, marked "—" — dropping them makes an optional question a host stopped
// getting answers to look like one they never asked.
// ⚠ Rendered by the SAME formatter the guest answers matrix uses (choice labels, dates).
import React, { useCallback, useEffect, useState } from "react";
import { Paper, Skeleton, Stack, Text } from "@mantine/core";
import { OpResult } from "@sdflc/api-helpers";

import { RsvpQuestion, RsvpQuestionAnswer, RsvpQuestionService } from "@/utils/api";
import { answerPicture, formatAnswer } from "@/components/features/EventGuests/answerFormat";
import { AnswerPicture } from "@/components/features/EventGuests/AnswerPicture";

import { ApplicationDetailRow } from "./ApplicationDetailRow";

export interface ApplicationAnswersSectionProps {
  eventId?: string | null;
  /** Which form the questions live on (QUESTION_ASKED_ON). */
  askedOn: string;
  /** Loads THIS applicant's answers — keyed on the application. Must be stable (useCallback) or it reloads. */
  loadAnswers: () => Promise<OpResult>;
  /** Narrow to the questions this applicant was actually asked (e.g. a sponsorship's partner kind). */
  questionFilter?: (q: RsvpQuestion) => boolean;
  title: string;
}

export const ApplicationAnswersSection: React.FC<ApplicationAnswersSectionProps> = React.memo(
  ({ eventId, askedOn, loadAnswers, questionFilter, title }) => {
    const [questions, setQuestions] = useState<RsvpQuestion[]>([]);
    const [answers, setAnswers] = useState<Record<string, RsvpQuestionAnswer>>({});
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
      if (!eventId) {
        return;
      }

      setLoading(true);

      const [qRes, aRes] = await Promise.all([RsvpQuestionService.list(eventId, askedOn), loadAnswers()]);
      const qs = qRes.code === 0 && Array.isArray(qRes.data) ? (qRes.data as RsvpQuestion[]) : [];
      const as = aRes.code === 0 && Array.isArray(aRes.data) ? (aRes.data as RsvpQuestionAnswer[]) : [];
      const byQuestion: Record<string, RsvpQuestionAnswer> = {};

      for (const a of as) {
        if (a.rsvpQuestionId) {
          byQuestion[a.rsvpQuestionId] = a;
        }
      }

      setQuestions(questionFilter ? qs.filter(questionFilter) : qs);
      setAnswers(byQuestion);
      setLoading(false);
    }, [eventId, askedOn, loadAnswers, questionFilter]);

    useEffect(() => {
      void load();
    }, [load]);

    if (loading) {
      return <Skeleton height={80} radius="md" />;
    }

    if (questions.length === 0) {
      return null;
    }

    return (
      <Paper withBorder radius="md" p="md">
        <Stack gap="sm">
          <Text size="md" fw={600}>
            {title}
          </Text>
          {questions.map((q) => {
            const answer = answers[q.id as string];
            const picture = answerPicture(q, answer);

            return (
              <ApplicationDetailRow key={q.id} label={q.title || ""}>
                {picture ? (
                  <AnswerPicture picture={picture} label={q.title} />
                ) : (
                  <Text size="md" style={{ whiteSpace: "pre-line" }}>
                    {formatAnswer(q, answer) || "—"}
                  </Text>
                )}
              </ApplicationDetailRow>
            );
          })}
        </Stack>
      </Paper>
    );
  },
);

ApplicationAnswersSection.displayName = "ApplicationAnswersSection";
